import React from "react";
import { Search, RefreshCw, Settings, BarChart3, Loader } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import SearchBar from "../../../components/SearchBar";
import TabsBar from "./TabsBar";

const FiltersBar = ({
  theme, isRTL, t, language,
  searchTerm, onSearchChange,
  filterType, onFilterTypeChange,
  filteredCount, totalCount,
  onRefresh, isLoading,
  activeTab, onTabChange,
}) => {
  const statCard = {
    background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
    padding: "1.55rem",
    //marginLeft: isRTL ? 0 : 20,
    //marginRight: isRTL ? 20 : 0,
    borderRadius: "1rem",
    transition: "all 0.3s ease",
    // boxShadow: theme === "dark"
    //   ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
    //   : "0 0.5rem 2rem rgba(0,0,0,0.1)",
    backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
    WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
    border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
    marginBottom: 70,
    direction: isRTL ? "rtl" : "ltr",
  };

  const controlsHeader = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: "clamp(20px, 4vw, 32px)", paddingBottom: "clamp(12px, 2.5vw, 20px)",
    borderBottom: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
    flexWrap: "wrap", gap: "16px",
  };

  const controlsTitle = {
    fontSize: "clamp(16px, 3vw, 20px)", fontWeight: "700",
    color: theme === "dark" ? "#ffffff" : "#0f172a",
    margin: 0, display: "flex", alignItems: "center",
    gap: "clamp(6px, 1vw, 10px)", letterSpacing: "0.3px",
  };

  const controlsSubtitle = {
    fontSize: "clamp(12px, 1.5vw, 14px)",
    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)",
    margin: "4px 0 0 0", textAlign: isRTL ? "right" : "left",
  };

  const controlsGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "clamp(16px, 3vw, 24px)", alignItems: "end",
    position: "relative", zIndex: 1,
  };

  const controlGroup = {
    display: "flex", flexDirection: "column",
    gap: "clamp(8px, 1.5vw, 12px)", flex: 1, minWidth: 0,
  };

  const label = {
    color: theme === "dark" ? "rgba(255,255,255,0.9)" : "#374151",
    fontSize: "clamp(12px, 1.5vw, 14px)", fontWeight: "700",
    letterSpacing: "0.3px", textTransform: "uppercase",
    display: "flex", alignItems: "center", gap: "clamp(4px, 1vw, 8px)",
  };

  const primaryButton = {
    background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
    color: "white", border: "none",
    borderRadius: "clamp(10px, 1.5vw, 14px)",
    padding: "clamp(10px, 2vw, 14px) clamp(16px, 3vw, 24px)",
    fontSize: "clamp(11px, 1.5vw, 14px)", fontWeight: "700",
    cursor: "pointer", display: "flex", alignItems: "center",
    gap: "clamp(6px, 1vw, 10px)", transition: "all 0.3s ease",
    letterSpacing: "0.5px", textTransform: "uppercase",
    minWidth: "clamp(100px, 15vw, 140px)", justifyContent: "center",
    whiteSpace: "nowrap",
  };

  const resultsInfo = {
    fontSize: "clamp(12px, 1.5vw, 14px)",
    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)",
    display: "flex", alignItems: "center", gap: "6px", fontWeight: "500",
  };

  const leaveTypeItems = [
    { id: "all",       name: t.allTypes },
    { id: "annual",    name: t.annual },
    { id: "sick",      name: t.sick },
    { id: "maternity", name: t.maternity },
    { id: "emergency", name: t.emergency },
    { id: "wfh",       name: t.wfh },
  ];

  return (
    <div style={{...statCard,marginTop:25}}>
      <div style={controlsHeader}>
        <div>
          <h3 style={controlsTitle}>
            <Settings size={window.innerWidth < 768 ? 16 : 20} />
            {t.searchAndFilter}
          </h3>
          <p style={controlsSubtitle}>{t.findAndFilter}</p>
        </div>
        <div style={resultsInfo}>
          <BarChart3 size={window.innerWidth < 768 ? 14 : 16} />
          {t.showing} {filteredCount} {t.of} {totalCount} {t.requests}
        </div>
      </div>

      <div className="nx-stack-mobile" style={controlsGrid}>
        {/* Search */}
        <div style={controlGroup}>
          <label style={label}>
            <Search size={window.innerWidth < 768 ? 14 : 16} />
            {t.searchEmployee}
          </label>
          <SearchBar
            value={searchTerm}
            onChange={onSearchChange}
            placeholder={t.searchPlaceholder}
            theme={theme}
            language={language}
            icon={true}
          />
        </div>

        {/* Leave Type Dropdown */}
        <div style={{ ...controlGroup, position: "relative", zIndex: 999, isolation: "isolate" }}>
          <label style={label}>{t.leaveType}</label>
          <div style={{ position: "relative", zIndex: 999 }}>
            <ReusableDropdown
              items={leaveTypeItems}
              value={filterType}
              onChange={(val) => onFilterTypeChange(val)}
              onSelect={(item) => {
                const value = typeof item === "string" ? item : item?.id || String(item);
                onFilterTypeChange(value);
              }}
              getItemKey={(it) => (typeof it === "string" ? it : it.id || String(it))}
              getItemLabel={(it) => (typeof it === "string" ? it : it.name || String(it))}
              placeholder={t.selectLeaveType || "اختر نوع الإجازة..."}
              theme={theme}
              language={language}
              maxItems={10}
            />
          </div>
        </div>
      </div>

      {/* Tabs Filters Section */}
      <div style={{ marginTop: "32px", paddingTop: "24px", borderTop: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>
        <TabsBar
          activeTab={activeTab}
          onTabChange={onTabChange}
          theme={theme}
          isRTL={isRTL}
          t={t}
        />
      </div>
    </div>
  );
};

export default FiltersBar;